/**
 * Animation Refinement System
 * Automatically optimizes animation timings and sequences based on user feedback and performance data
 */

import { uxTester, type AnimationRefinement, type UXTestSession } from './uxTesting';
import { performanceOptimizer } from './performanceOptimizer';
import { getBrowserInfo } from './browserCompatibility';

export interface RefinementRule {
  id: string;
  name: string;
  description: string;
  condition: (data: RefinementAnalysisData) => boolean;
  apply: (element: HTMLElement, data: RefinementAnalysisData) => RefinementResult;
  priority: number; // 1-10, higher is more important
}

export interface RefinementAnalysisData {
  element: HTMLElement;
  selector: string;
  userSatisfaction: number;
  averageInteractionTime: number;
  errorRate: number;
  completionRate: number;
  performanceMetrics: {
    fps: number;
    memoryUsage: number;
    droppedFrames: number;
  };
  userFeedback: string[];
  browserInfo: any;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

export interface RefinementResult {
  applied: boolean;
  changes: Array<{
    property: string;
    oldValue: string;
    newValue: string;
    reason: string;
  }>;
  expectedImprovement: string;
  confidence: number; // 0-1
}

export interface RefinementReport {
  timestamp: number;
  totalElementsAnalyzed: number;
  refinementsApplied: number;
  averageConfidence: number;
  expectedImprovements: string[];
  detailedResults: Array<{
    selector: string;
    result: RefinementResult;
  }>;
}

class AnimationRefinementEngine {
  private refinementRules: Map<string, RefinementRule> = new Map();
  private appliedRefinements: Map<string, RefinementResult[]> = new Map();
  private refinementHistory: RefinementReport[] = [];

  constructor() {
    this.initializeDefaultRules();
  }

  private initializeDefaultRules(): void {
    // Rule 1: Speed up slow animations based on user feedback
    this.addRule({
      id: 'speed-up-slow-animations',
      name: 'Speed Up Slow Animations',
      description: 'Reduces duration of animations that users find too slow',
      condition: (data) => data.userSatisfaction < 3 && data.averageInteractionTime > 800,
      apply: (element, data) => {
        const currentDuration = this.getCurrentAnimationDuration(element);
        const newDuration = Math.max(200, currentDuration * 0.7);
        
        this.applyAnimationDuration(element, newDuration);
        
        return {
          applied: true,
          changes: [{
            property: 'animation-duration',
            oldValue: `${currentDuration}ms`,
            newValue: `${newDuration}ms`,
            reason: 'User feedback indicated animation was too slow'
          }],
          expectedImprovement: 'Faster, more responsive feel',
          confidence: 0.8
        };
      },
      priority: 8
    });

    // Rule 2: Slow down fast animations that cause errors
    this.addRule({
      id: 'slow-down-error-prone-animations',
      name: 'Slow Down Error-Prone Animations',
      description: 'Increases duration of animations with high error rates',
      condition: (data) => data.errorRate > 0.15 && data.averageInteractionTime < 300,
      apply: (element, data) => {
        const currentDuration = this.getCurrentAnimationDuration(element);
        const newDuration = Math.min(1000, currentDuration * 1.3);
        
        this.applyAnimationDuration(element, newDuration);
        
        return {
          applied: true,
          changes: [{
            property: 'animation-duration',
            oldValue: `${currentDuration}ms`,
            newValue: `${newDuration}ms`,
            reason: 'High error rate suggests animation is too fast for users to follow'
          }],
          expectedImprovement: 'Reduced user errors and confusion',
          confidence: 0.7
        };
      },
      priority: 7
    });

    // Rule 3: Improve easing for better perceived performance
    this.addRule({
      id: 'optimize-easing-curves',
      name: 'Optimize Easing Curves',
      description: 'Adjusts easing functions based on user satisfaction',
      condition: (data) => data.userSatisfaction < 3.5 && data.completionRate > 0.8,
      apply: (element, data) => {
        const currentEasing = this.getCurrentEasing(element);
        const newEasing = this.suggestBetterEasing(currentEasing, data);
        
        this.applyEasing(element, newEasing);
        
        return {
          applied: true,
          changes: [{
            property: 'animation-timing-function',
            oldValue: currentEasing,
            newValue: newEasing,
            reason: 'Optimized easing curve for better user perception'
          }],
          expectedImprovement: 'More natural and pleasing animation feel',
          confidence: 0.6
        };
      },
      priority: 6
    });

    // Rule 4: Add stagger delays for better visual hierarchy
    this.addRule({
      id: 'add-stagger-delays',
      name: 'Add Stagger Delays',
      description: 'Adds staggered timing to grouped animations',
      condition: (data) => {
        const siblings = this.getSimilarElements(data.element);
        return siblings.length > 2 && data.userSatisfaction < 3.5;
      },
      apply: (element, data) => {
        const siblings = this.getSimilarElements(element);
        const staggerDelay = 100; // ms between each element
        
        siblings.forEach((sibling, index) => {
          this.applyAnimationDelay(sibling, index * staggerDelay);
        });
        
        return {
          applied: true,
          changes: [{
            property: 'animation-delay',
            oldValue: '0ms',
            newValue: `staggered (0-${(siblings.length - 1) * staggerDelay}ms)`,
            reason: 'Added stagger effect for better visual hierarchy'
          }],
          expectedImprovement: 'Clearer visual progression and hierarchy',
          confidence: 0.7
        };
      },
      priority: 5
    });

    // Rule 5: Reduce motion for accessibility
    this.addRule({
      id: 'reduce-motion-accessibility',
      name: 'Reduce Motion for Accessibility',
      description: 'Simplifies animations for users with motion sensitivity',
      condition: (data) => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        return prefersReducedMotion || data.userFeedback.some(f => f.includes('dizzy') || f.includes('motion'));
      },
      apply: (element, data) => {
        const changes = [];
        
        // Reduce or eliminate transforms
        const currentTransform = getComputedStyle(element).transform;
        if (currentTransform !== 'none') {
          element.style.transform = 'none';
          changes.push({
            property: 'transform',
            oldValue: currentTransform,
            newValue: 'none',
            reason: 'Reduced motion for accessibility compliance'
          });
        }
        
        // Shorten duration significantly
        const currentDuration = this.getCurrentAnimationDuration(element);
        const newDuration = Math.min(200, currentDuration * 0.2);
        this.applyAnimationDuration(element, newDuration);
        
        changes.push({
          property: 'animation-duration',
          oldValue: `${currentDuration}ms`,
          newValue: `${newDuration}ms`,
          reason: 'Shortened duration for motion sensitivity'
        });
        
        return {
          applied: true,
          changes,
          expectedImprovement: 'Better accessibility for motion-sensitive users',
          confidence: 0.9
        };
      },
      priority: 10
    });

    // Rule 6: Optimize for mobile performance
    this.addRule({
      id: 'optimize-mobile-performance',
      name: 'Optimize Mobile Performance',
      description: 'Reduces animation complexity on mobile devices',
      condition: (data) => {
        return data.deviceType === 'mobile' && 
               (data.performanceMetrics.fps < 30 || data.performanceMetrics.droppedFrames > 10);
      },
      apply: (element, data) => {
        const changes = [];
        
        // Simplify transforms to only use translate and scale
        const style = getComputedStyle(element);
        if (style.transform.includes('rotate') || style.transform.includes('skew')) {
          element.style.transform = element.style.transform
            .replace(/rotate[XYZ]?\([^)]+\)/g, '')
            .replace(/skew[XY]?\([^)]+\)/g, '');
          
          changes.push({
            property: 'transform',
            oldValue: 'complex 3D transforms',
            newValue: 'simplified 2D transforms',
            reason: 'Reduced complexity for mobile performance'
          });
        }
        
        // Reduce concurrent animations
        const duration = this.getCurrentAnimationDuration(element);
        this.applyAnimationDuration(element, duration * 1.2);
        
        changes.push({
          property: 'animation-duration',
          oldValue: `${duration}ms`,
          newValue: `${duration * 1.2}ms`,
          reason: 'Slightly longer duration to reduce mobile CPU load'
        });
        
        return {
          applied: true,
          changes,
          expectedImprovement: 'Better performance on mobile devices',
          confidence: 0.8
        };
      },
      priority: 7
    });

    // Rule 7: Enhance hover feedback
    this.addRule({
      id: 'enhance-hover-feedback',
      name: 'Enhance Hover Feedback',
      description: 'Improves hover animations based on user interaction patterns',
      condition: (data) => {
        return data.deviceType === 'desktop' && 
               data.userSatisfaction < 3.5 && 
               data.averageInteractionTime > 500;
      },
      apply: (element, data) => {
        // Add or enhance hover effects
        const hoverDuration = 200;
        const hoverScale = 1.05;
        
        // Create hover styles if they don't exist
        if (!element.style.transition.includes('transform')) {
          element.style.transition = `${element.style.transition}, transform ${hoverDuration}ms ease-out`.replace(/^, /, '');
        }
        
        // Add hover event listeners
        element.addEventListener('mouseenter', () => {
          element.style.transform = `${element.style.transform} scale(${hoverScale})`;
        });
        
        element.addEventListener('mouseleave', () => {
          element.style.transform = element.style.transform.replace(/scale\([^)]+\)/, '');
        });
        
        return {
          applied: true,
          changes: [{
            property: 'hover-effect',
            oldValue: 'none or weak',
            newValue: `scale(${hoverScale}) with ${hoverDuration}ms transition`,
            reason: 'Enhanced hover feedback for better interactivity'
          }],
          expectedImprovement: 'More responsive and engaging interactions',
          confidence: 0.6
        };
      },
      priority: 4
    });
  }

  private addRule(rule: RefinementRule): void {
    this.refinementRules.set(rule.id, rule);
  }

  private getCurrentAnimationDuration(element: HTMLElement): number {
    const style = getComputedStyle(element);
    const duration = style.animationDuration || style.transitionDuration || '0s';
    return parseFloat(duration) * (duration.includes('ms') ? 1 : 1000);
  }

  private getCurrentEasing(element: HTMLElement): string {
    const style = getComputedStyle(element);
    return style.animationTimingFunction || style.transitionTimingFunction || 'ease';
  }

  private applyAnimationDuration(element: HTMLElement, duration: number): void {
    const durationStr = `${duration}ms`;
    
    if (element.style.animationDuration) {
      element.style.animationDuration = durationStr;
    }
    
    if (element.style.transitionDuration) {
      element.style.transitionDuration = durationStr;
    }
    
    // Also update CSS custom property if used
    element.style.setProperty('--animation-duration', durationStr);
  }

  private applyAnimationDelay(element: HTMLElement, delay: number): void {
    const delayStr = `${delay}ms`;
    element.style.animationDelay = delayStr;
    element.style.setProperty('--animation-delay', delayStr);
  }

  private applyEasing(element: HTMLElement, easing: string): void {
    if (element.style.animationTimingFunction !== undefined) {
      element.style.animationTimingFunction = easing;
    }
    
    if (element.style.transitionTimingFunction !== undefined) {
      element.style.transitionTimingFunction = easing;
    }
  }

  private suggestBetterEasing(currentEasing: string, data: RefinementAnalysisData): string {
    // Suggest easing based on user satisfaction and interaction patterns
    if (data.errorRate > 0.1) {
      return 'ease-out'; // Slower end for better control
    }
    
    if (data.averageInteractionTime > 600) {
      return 'ease-in-out'; // More natural feeling
    }
    
    if (data.userSatisfaction < 2.5) {
      return [0.25, 0.46, 0.45, 0.94]; // More pleasing curve
    }
    
    return 'ease-out'; // Safe default
  }

  private getSimilarElements(element: HTMLElement): HTMLElement[] {
    const className = element.className.split(' ')[0];
    const tagName = element.tagName.toLowerCase();
    
    // Find elements with same class or tag in the same container
    const container = element.parentElement;
    if (!container) return [element];
    
    const selector = className ? `.${className}` : tagName;
    const similar = Array.from(container.querySelectorAll(selector)) as HTMLElement[];
    
    return similar.length > 1 ? similar : [element];
  }

  private getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const width = window.innerWidth;
    const userAgent = navigator.userAgent;
    
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      return width > 768 ? 'tablet' : 'mobile';
    }
    
    return width > 1024 ? 'desktop' : 'tablet';
  }

  private analyzeElement(element: HTMLElement): RefinementAnalysisData {
    const selector = this.getElementSelector(element);
    const sessions = uxTester.getAllSessions();
    const performanceMetrics = performanceOptimizer.getMetrics();
    
    // Aggregate data from all sessions for this element
    let totalSatisfaction = 0;
    let satisfactionCount = 0;
    let totalInteractionTime = 0;
    let interactionCount = 0;
    let totalErrors = 0;
    let totalCompletions = 0;
    const feedbackComments: string[] = [];
    
    sessions.forEach(session => {
      session.interactions.forEach(interaction => {
        if (interaction.element === selector || interaction.animationTriggered === selector) {
          if (interaction.userSatisfaction) {
            totalSatisfaction += interaction.userSatisfaction;
            satisfactionCount++;
          }
          
          if (interaction.duration) {
            totalInteractionTime += interaction.duration;
            interactionCount++;
            totalCompletions++;
          }
        }
      });
      
      session.feedback.forEach(feedback => {
        if (feedback.element === selector) {
          if (feedback.comment) {
            feedbackComments.push(feedback.comment);
          }
        }
      });
      
      totalErrors += session.metrics.errorRate * session.metrics.totalInteractions;
    });
    
    return {
      element,
      selector,
      userSatisfaction: satisfactionCount > 0 ? totalSatisfaction / satisfactionCount : 3,
      averageInteractionTime: interactionCount > 0 ? totalInteractionTime / interactionCount : 0,
      errorRate: interactionCount > 0 ? totalErrors / interactionCount : 0,
      completionRate: interactionCount > 0 ? totalCompletions / interactionCount : 1,
      performanceMetrics: {
        fps: performanceMetrics.averageFPS,
        memoryUsage: performanceMetrics.memoryUsage,
        droppedFrames: performanceMetrics.frameDrops
      },
      userFeedback: feedbackComments,
      browserInfo: getBrowserInfo(),
      deviceType: this.getDeviceType()
    };
  }

  private getElementSelector(element: HTMLElement): string {
    if (element.id) return `#${element.id}`;
    if (element.className) return `.${element.className.split(' ')[0]}`;
    return element.tagName.toLowerCase();
  }

  // Public API
  public analyzeAndRefine(elements?: HTMLElement[]): RefinementReport {
    const elementsToAnalyze = elements || this.findAnimatedElements();
    const report: RefinementReport = {
      timestamp: Date.now(),
      totalElementsAnalyzed: elementsToAnalyze.length,
      refinementsApplied: 0,
      averageConfidence: 0,
      expectedImprovements: [],
      detailedResults: []
    };

    let totalConfidence = 0;
    
    elementsToAnalyze.forEach(element => {
      const analysisData = this.analyzeElement(element);
      const applicableRules = this.getApplicableRules(analysisData);
      
      // Apply rules in priority order
      applicableRules.sort((a, b) => b.priority - a.priority);
      
      for (const rule of applicableRules) {
        const result = rule.apply(element, analysisData);
        
        if (result.applied) {
          report.refinementsApplied++;
          totalConfidence += result.confidence;
          report.expectedImprovements.push(result.expectedImprovement);
          
          report.detailedResults.push({
            selector: analysisData.selector,
            result
          });
          
          // Store applied refinement
          const elementRefinements = this.appliedRefinements.get(analysisData.selector) || [];
          elementRefinements.push(result);
          this.appliedRefinements.set(analysisData.selector, elementRefinements);
          
          break; // Apply only one rule per element per analysis
        }
      }
    });
    
    report.averageConfidence = report.refinementsApplied > 0 ? totalConfidence / report.refinementsApplied : 0;
    this.refinementHistory.push(report);
    
    return report;
  }

  private findAnimatedElements(): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const allElements = document.querySelectorAll('*');
    
    allElements.forEach(el => {
      const element = el as HTMLElement;
      const style = getComputedStyle(element);
      
      if (style.animation !== 'none' || 
          style.transition !== 'none' || 
          element.classList.toString().includes('animate') ||
          element.hasAttribute('data-animate')) {
        elements.push(element);
      }
    });
    
    return elements;
  }

  private getApplicableRules(data: RefinementAnalysisData): RefinementRule[] {
    const applicableRules: RefinementRule[] = [];
    
    this.refinementRules.forEach(rule => {
      if (rule.condition(data)) {
        applicableRules.push(rule);
      }
    });
    
    return applicableRules;
  }

  public getRefinementHistory(): RefinementReport[] {
    return [...this.refinementHistory];
  }

  public getAppliedRefinements(selector?: string): Map<string, RefinementResult[]> {
    if (selector) {
      const refinements = this.appliedRefinements.get(selector);
      return refinements ? new Map([[selector, refinements]]) : new Map();
    }
    
    return new Map(this.appliedRefinements);
  }

  public revertRefinements(selector: string): boolean {
    const refinements = this.appliedRefinements.get(selector);
    if (!refinements) return false;
    
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) return false;
    
    // Revert changes (this is simplified - in practice, you'd need to store original values)
    refinements.forEach(refinement => {
      refinement.changes.forEach(change => {
        if (change.property === 'animation-duration') {
          element.style.animationDuration = change.oldValue;
        } else if (change.property === 'animation-timing-function') {
          element.style.animationTimingFunction = change.oldValue;
        }
        // Add more property reversions as needed
      });
    });
    
    this.appliedRefinements.delete(selector);
    return true;
  }

  public addCustomRule(rule: RefinementRule): void {
    this.addRule(rule);
  }

  public removeRule(ruleId: string): boolean {
    return this.refinementRules.delete(ruleId);
  }

  public exportRefinementData(): string {
    return JSON.stringify({
      history: this.refinementHistory,
      appliedRefinements: Array.from(this.appliedRefinements.entries()),
      rules: Array.from(this.refinementRules.entries()).map(([id, rule]) => ({
        id,
        name: rule.name,
        description: rule.description,
        priority: rule.priority
      }))
    }, null, 2);
  }
}

// Export singleton instance
export const animationRefiner = new AnimationRefinementEngine();

// Utility functions
export const refineAnimations = (elements?: HTMLElement[]) => animationRefiner.analyzeAndRefine(elements);
export const getRefinementHistory = () => animationRefiner.getRefinementHistory();
export const getAppliedRefinements = (selector?: string) => animationRefiner.getAppliedRefinements(selector);
export const revertAnimationRefinements = (selector: string) => animationRefiner.revertRefinements(selector);
export const addRefinementRule = (rule: RefinementRule) => animationRefiner.addCustomRule(rule);
export const exportRefinementData = () => animationRefiner.exportRefinementData();